import { useState } from 'react';
import { Loader2, AlertTriangle, Trash2, Unplug } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';


export function XeroConnectionsPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [revoking, setRevoking] = useState(null);

  const fetchConnections = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await diagnosticsAPI.xeroConnections();
      setData(d);
      if (d.error) {
        setError(d.error);
      }
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (connectionId, tenantName) => {
    if (!window.confirm(`Revoke connection for "${tenantName}"? This will remove the connection at Xero.`)) {
      return;
    }
    setRevoking(connectionId);
    try {
      await diagnosticsAPI.xeroConnectionRevoke(connectionId);
      toast.success(`Revoked connection for ${tenantName}`);
      await fetchConnections();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRevoking(null);
    }
  };

  const cap = data?.cap || 25;
  const count = data?.count || 0;
  const headroom = cap - count;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Unplug className="h-5 w-5" />
          Xero Connections
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Live connections registered on the Xero app. Uncertified apps cap at 25 organisations.
          Use Revoke to remove stale connections and free slots.
        </p>

        <Button
          variant="outline"
          size="sm"
          onClick={fetchConnections}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Load Connections
        </Button>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}

        {data && !error && (
          <div className="space-y-3">
            {/* Cap counter */}
            <div className="flex items-center gap-3">
              <Badge variant={headroom <= 3 ? 'destructive' : 'default'} className="text-xs">
                {count} / {cap} connections used
              </Badge>
              <span className="text-xs text-muted-foreground">
                {headroom} slot{headroom !== 1 ? 's' : ''} remaining
              </span>
            </div>

            {/* Connections table */}
            {data.connections.length === 0 ? (
              <p className="text-sm text-muted-foreground">No connections registered.</p>
            ) : (
              <div className="border rounded overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 text-left">
                      <th className="px-3 py-2 font-medium">Org Name</th>
                      <th className="px-3 py-2 font-medium">Tenant ID</th>
                      <th className="px-3 py-2 font-medium">Connection ID</th>
                      <th className="px-3 py-2 font-medium">Created</th>
                      <th className="px-3 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.connections.map((c) => (
                      <tr key={c.connectionId} className="border-t">
                        <td className="px-3 py-2">{c.tenantName || '(unnamed)'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{c.tenantId}</td>
                        <td className="px-3 py-2 font-mono text-xs">{c.connectionId}</td>
                        <td className="px-3 py-2 text-xs">
                          {c.createdDateUtc ? new Date(c.createdDateUtc).toLocaleString() : ''}
                        </td>
                        <td className="px-3 py-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRevoke(c.connectionId, c.tenantName || c.tenantId)}
                            disabled={revoking === c.connectionId}
                          >
                            {revoking === c.connectionId ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <Trash2 className="h-3 w-3 mr-1" />
                            )}
                            Revoke
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
